export const CURRENT = {
  id: 'amazon',
  org: 'Amazon',
  logo: '/logos/amazon.png',
  role: 'Software Engineer Intern',
  location: 'Seattle, WA',
  dates: 'September 2026 to present',
  theme: { bg: '#232F3E', ink: '#FF9900', text: '#FFFFFF', dim: '#D5DBDB' },
  sections: [
    {
      blocks: [
        'I work on the systems behind employee compliance at Amazon. The rules vary by country and are often set by law, so changing one has traditionally meant a ticket, an engineer, a code review, and manual testing.',
      ],
    },
    {
      heading: 'Rule management',
      blocks: [
        'I mapped out and built a Python platform that lets HR service teams change these rules in the code itself, without engineering help. They describe the task in natural language, and it gets built.',
      ],
    },
    {
      heading: 'End-to-end validation',
      blocks: [
        'Once changes got faster, validating them became the bottleneck. AI-generated unit tests tend to pass by design: they check that code does what it says, not that a feature works across real services.',
        'I proposed an AI testing platform to close that gap, then designed and built it on my own on AWS. A team uploads a requirements document, and AI agents running on AWS Lambda and Amazon Bedrock map the system, write the tests, run them end to end, and verify the results in a pre-production environment.',
      ],
    },
  ],
  stack: ['Python', 'Java', 'Kotlin', 'AWS Lambda', 'Amazon Bedrock'],
  technical: [
    [
      'Each agent runs as its own AWS Lambda function, so stages can deploy, scale, and retry on their own.',
      'Models on Amazon Bedrock generate tests from how the system is actually wired, not from what the code claims about itself.',
      'Test cases cover both happy paths and failure paths.',
      'Everything runs on synthetic data, never real employee or customer data.',
      'Results show up in code review, so reviewers see evidence without leaving the review.',
    ],
  ],
};

export const INCOMING = {
  id: 'gemini',
  org: 'Gemini',
  logo: '/logos/gemini.png',
  role: 'Incoming Software Engineer Intern',
  location: 'Remote/New York',
  dates: 'Winter 2027',
  theme: { bg: '#FF4809', ink: '#000000', text: '#000000', dim: '#2B0F00', tile: '#FF4809' },
  sections: [
    {
      blocks: [
        'Incoming Backend SWE Intern on Gemini’s Credit Card team (Winter 2027), particularly planned to build low-latency C++ infrastructure for the card’s crypto cash-back system, which converts purchase rewards into the cardholder’s chosen cryptocurrency in real time.',
      ],
    },
  ],
  preview: {
    href: 'https://www.gemini.com/credit-card',
    image: '/previews/gemini-credit-card.jpg',
    title: 'Earn Crypto Rewards with the Gemini Credit Card®',
    description: 'Learn more about the benefits of Gemini’s Credit card, a crypto rewards credit card and apply today.',
    site: 'gemini.com/credit-card',
  },
};

export const PREVIOUS = [
  {
    id: 'mongodb',
    org: 'MongoDB',
    logo: '/logos/mongodb.png',
    role: 'Software Engineer Intern',
    location: 'New York, NY',
    dates: 'June to August 2025',
    theme: { bg: '#001E2B', ink: '#00ED64', text: '#FFFFFF', dim: '#C1D2D9' },
    summary: 'Moved learn.mongodb.com off AWS Lambda and onto Kubernetes so certification exams hold up under event traffic.',
    sections: [
      {
        heading: 'The problem',
        blocks: [
          'I was on the team behind learn.mongodb.com, MongoDB’s learning and certification platform, where people take courses, sit certification exams, and earn badges.',
          'The backend was a FastAPI app on AWS Lambda, deployed through the Mangum adapter. As traffic and integrations grew, it hit its limits. The worst failures came during MongoDB.local events in cities like New York and London, when traffic spikes overloaded the site and exams wouldn’t load. People came to those events to get certified on the spot, so this broke the core experience.',
        ],
      },
      {
        heading: 'The migration',
        blocks: [
          'I owned rebuilding the backend and moving it off Lambda onto Kanopy, MongoDB’s internal Kubernetes platform. The project ran about 14 weeks in four phases, from design and roadmap review with senior engineers through to production.',
          'I containerized the service with Docker and set up CI/CD with Drone. I also introduced code quality standards: mypy for static type checking, pylint for linting, and black and isort for formatting. The service connects to several external services and to MongoDB Atlas, and the migration moved authentication to a new JWT server.',
        ],
      },
      {
        heading: 'The result',
        blocks: [
          'The platform went from a serverless model that broke under event traffic to a containerized Kubernetes service built to handle that load reliably. Along the way I consolidated 50+ endpoints, and the API handles 12K+ requests a day.',
        ],
      },
    ],
    stack: ['Python', 'FastAPI', 'Docker', 'Kubernetes', 'Drone', 'MongoDB Atlas', 'JWT'],
  },
  {
    id: 'rakuten',
    org: 'Rakuten',
    logo: '/logos/rakuten.png',
    role: 'Software Engineer Intern',
    location: 'San Mateo, CA',
    dates: 'January to May 2025',
    theme: { bg: '#BF0000', ink: '#FFFFFF', text: '#FFFFFF', dim: '#FFE0E0' },
    summary: 'Built a production AI assistant that answers onboarding questions from internal documents.',
    sections: [
      {
        heading: 'The assistant',
        blocks: [
          'I interned part-time at Rakuten Securities, about 25 hours a week alongside school. My main project was a production AI assistant, built in Python and Java, that answers employee and onboarding questions using the company’s internal documents.',
          'New hires and employees could get answers about internal processes themselves, instead of digging through documents or asking colleagues.',
        ],
      },
      {
        heading: 'How it works',
        blocks: [
          'The core is a retrieval-augmented generation (RAG) pipeline built with LangChain. Internal documents are split into chunks, embedded, and indexed, so the assistant can pull the most relevant passages at query time. That keeps answers grounded in real company knowledge rather than whatever the model would generate on its own.',
          'A PyTorch intent classifier identifies what kind of question is being asked and routes it to the right handling path. I also added OpenAI Whisper so people could ask questions by voice.',
          'On the data side, I worked with the Spark and Hadoop ETL pipelines that refresh the data behind the knowledge base, cutting data latency by 60%.',
        ],
      },
    ],
    stack: ['Python', 'Java', 'LangChain', 'PyTorch', 'Whisper', 'Spark', 'Hadoop'],
  },
  {
    id: 'alaska',
    org: 'Alaska Airlines',
    logo: '/logos/alaska.png',
    role: 'Software Engineer Intern',
    location: 'Seattle, WA',
    dates: 'June to September 2024',
    theme: { bg: '#01426A', ink: '#FFFFFF', text: '#FFFFFF', dim: '#CFE8F2' },
    summary: 'Built a customer support chatbot, check-in features, and a seat-selection map.',
    sections: [
      {
        heading: 'Customer support chatbot',
        blocks: [
          'I built an AI chatbot using OpenAI GPT through LangChain, with FastAPI and AWS, to automate customer support. It improved service efficiency by 25%.',
        ],
      },
      {
        heading: 'Check-in',
        blocks: [
          'I built several check-in features for the Alaska Airlines mobile app using React Native and Go, with SQL storing and retrieving check-in records. Together they cut check-in time by 10%.',
        ],
      },
      {
        heading: 'Seat selection',
        blocks: [
          'I built the frontend for a new dynamic seat-selection map on the website using React and TypeScript. It made seat maps easier to reach and contributed to a projected 15% increase in seat selection revenue for Q3.',
        ],
      },
      {
        heading: 'Flight status notifications',
        blocks: [
          'I developed a flight status notification system using Node.js and AWS, which reduced notification delays by 20%.',
        ],
      },
    ],
    stack: ['React Native', 'React', 'TypeScript', 'Go', 'Node.js', 'FastAPI', 'LangChain', 'SQL', 'AWS'],
  },
];

export const PROJECTS = [
  {
    org: 'VibeScope',
    dates: 'June 2023',
    summary: 'YouTube comment sentiment analysis built with Hume AI, Flask, and React.',
    href: 'https://devpost.com/software/vibescope',
  },
];

export const EDUCATION = [
  {
    org: 'University of Illinois Urbana-Champaign',
    detail: 'MS in Computer Science, December 2027',
  },
  {
    org: 'University of California, Riverside',
    detail: 'BS in Computer Science, Minor in Business Analytics, June 2026',
  },
];

export const ENTRIES = [INCOMING, CURRENT, ...PREVIOUS];
